import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import Button from "./Button";
import Avatar from "@mui/material/Avatar";
import { BUTTON_CREATE_WITH_CANVA } from "../../i18n/keysTranslations";
import { useTranslationApp } from "../../lib/i18next";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import { logoPlatform } from "../../utils/string";
import { startConnectCanva, startGetTokenCanva } from "../../actions/user";
import { CANVA, MODE_BACK_DROP_CONNECT } from "../../utils/constants";
import IconButton from "./IconButton";
import AddIcon from "@mui/icons-material/Add";
import Loader from "../Loaders/Loader";
import BackDropLoader from "../Loaders/BackDropLoader";

const ButtonCanvaAction = ({
  id,
  text = BUTTON_CREATE_WITH_CANVA,
  prevBlockClick = false,
  onPrevBlockClick = () => {},
  onClick = () => {},
  onCallbackOpenPopup = () => {},
  onCallbackCancel = () => {},
  sx = {},
  logo = CANVA,
  hasShadow = true,
  type = "button",
  icon = <AddIcon />,
  loaderColor = "black",
  mode = "verify",
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [openBackDrop, setOpenBackDrop] = useState(false);
  const [externalPopup, setExternalPopup] = useState(null);
  const [modeBackDrop, setModeBackDrop] = useState();

  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const integrations = useSelectorApp((state) => state?.user?.integrations);
  const canvaIntegrations = integrations?.canva || {};
  const showCanvaOption =
    canvaIntegrations?.isActive === true ||
    Object.values(canvaIntegrations).some(
      (integration) =>
        integration && typeof integration === "object" && integration?.isActive
    );

  const onConnectCanva = () => {
    setModeBackDrop(MODE_BACK_DROP_CONNECT.GET_POPUP);
    dispatch(
      startConnectCanva({
        onChangeLoading: (loading) => {
          setIsLoading(loading);
          setOpenBackDrop(loading);
          if (!loading) {
            onCallbackOpenPopup();
          }
        },
        onChangePopupReference: (popup) => setExternalPopup(popup),
      })
    );
  };

  const onGetTokenCanva = async ({ code, state }) => {
    setModeBackDrop(MODE_BACK_DROP_CONNECT.GET_INFORMATION);
    setOpenBackDrop(true);
    const response = await dispatch(
      startGetTokenCanva({ code, state, withAuth: true })
    );
    setOpenBackDrop(false);

    if (response) {
      await onClick();
    }
  };

  useEffect(() => {
    if (!externalPopup) {
      return;
    }

    const timer = setInterval(() => {
      if (!externalPopup || externalPopup.closed) {
        timer && clearInterval(timer);
        if (externalPopup?.closed) {
          setOpenBackDrop(false);
          onCallbackCancel();
        }
        setExternalPopup(null);
        return;
      }

      try {
        const currentUrl = externalPopup.location.href;
        if (!currentUrl) {
          return;
        }
        const searchParams = new URL(currentUrl).searchParams;
        const code = searchParams.get("code");
        const state = searchParams.get("state");

        if (code && state) {
          externalPopup.close();
          setExternalPopup(null);
          clearInterval(timer);

          onGetTokenCanva({
            code,
            state,
          });
        }
      } catch (error) {
        if (externalPopup.closed) {
          clearInterval(timer);
          setExternalPopup(null);
        }
      }
    }, 500);

    return () => {
      timer && clearInterval(timer);
    };
    // eslint-disable-next-line
  }, [externalPopup]);

  const backdropPortal =
    openBackDrop &&
    createPortal(
      <BackDropLoader
        open={openBackDrop}
        platform={CANVA}
        from={modeBackDrop}
      />,
      document.body
    );

  if (type === "icon") {
    return (
      <>
        {backdropPortal}
        <IconButton
          id={id}
          sx={{ ...sx }}
          onClick={async (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (prevBlockClick) {
              onPrevBlockClick();
              return;
            }

            if (!(showCanvaOption && mode === "verify") || mode === "add") {
              onConnectCanva();
              return;
            }

            setIsLoading(true);
            await onClick();
            setIsLoading(false);
          }}
        >
          {isLoading ? (
            <Loader hasMarginTop={false} size={15} colorCustom={loaderColor} />
          ) : (
            icon
          )}
        </IconButton>
      </>
    );
  }

  return (
    <>
      {backdropPortal}
      <Button
        id={id}
        loading={isLoading}
        color="secondary"
        hasShadow={hasShadow}
        sx={{
          width: "100%",
          ...sx,
        }}
        onClick={async (e) => {
          e.preventDefault();
          if (prevBlockClick) {
            onPrevBlockClick();
            return;
          }

          if (!(showCanvaOption && mode === "verify") || mode === "add") {
            onConnectCanva();
            return;
          }

          setIsLoading(true);
          await onClick();
          setIsLoading(false);
        }}
      >
        <Avatar
          src={logoPlatform(logo)}
          variant="square"
          sx={{
            width: "20px",
            height: "20px",
            mr: 1,
            ".MuiAvatar-img": {
              objectFit: "contain",
            },
          }}
        />
        {t(text)}
      </Button>
    </>
  );
};

export default ButtonCanvaAction;
